import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import ChantierDetail from './ChantierDetail';
import {
  ArrowLeft, ClipboardList, FileText, CheckCircle2, HardHat, Send,
  Loader2, FileSignature, Calendar, MapPin, Users, Wrench,
  ChevronRight, Camera, X, Image as ImageIcon, Layers, Truck,
  Plus, Trash2, Save, Edit, Receipt, Link2, Unlink, Search,
  Briefcase, Package, Euro, TrendingUp, Settings, AlertTriangle, ShieldCheck,
} from 'lucide-react';

// 7 steps including the new "overview" at the start
const STEPS = [
  { key: 'overview',     label: "Vue d'ensemble", icon: Layers,        color: 'slate' },
  { key: 'visite',       label: 'Visite',         icon: ClipboardList, color: 'blue' },
  { key: 'devis',        label: 'Devis',          icon: FileText,      color: 'blue' },
  { key: 'envoye',       label: 'Envoyé',         icon: Send,          color: 'indigo' },
  { key: 'accepte',      label: 'Signé',          icon: CheckCircle2,  color: 'emerald' },
  { key: 'bon_commande', label: 'BC reçu',        icon: FileSignature, color: 'amber' },
  { key: 'chantier',     label: 'Chantier',       icon: HardHat,       color: 'orange' },
];

const STAGE_META = {
  visite:       { label: 'Visite faite',      color: 'bg-slate-100 text-slate-700' },
  devis:        { label: 'Devis brouillon',   color: 'bg-blue-100 text-blue-700' },
  envoye:       { label: 'Devis envoyé',      color: 'bg-indigo-100 text-indigo-700' },
  accepte:      { label: 'Devis signé',       color: 'bg-emerald-100 text-emerald-700' },
  bon_commande: { label: 'BC reçu',           color: 'bg-amber-100 text-amber-800' },
  chantier:     { label: 'Chantier',          color: 'bg-orange-100 text-orange-800' },
  refuse:       { label: 'Devis refusé',      color: 'bg-red-100 text-red-700' },
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return iso; }
};
const fmtMoney = (n) => n == null ? '-' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

export default function TravauxDepannage({ clientId, clientName, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [creating, setCreating] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/gestion-clients/${clientId}/travaux`);
      setData(res.data);
      if (selectedItem) {
        const updated = res.data.items?.find(
          (i) => (i.devis_id || '_') === (selectedItem.devis_id || '_') &&
                 (i.fiche_id || '_') === (selectedItem.fiche_id || '_')
        );
        if (updated) setSelectedItem(updated);
      }
    } catch (e) { console.error('travaux load error:', e); }
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { if (clientId) load(); }, [clientId]);

  const openItem = (item) => {
    setSelectedItem(item);
    setSelectedStep('overview');
  };
  const closeItem = () => { setSelectedItem(null); setSelectedStep(null); };

  const createDossier = async () => {
    setCreating(true);
    try {
      const r = await api.post('/travaux/dossier', {
        gestion_client_id: clientId,
        project_title: createTitle || 'Nouveau dossier travaux',
      });
      setShowCreate(false);
      setCreateTitle('');
      await load(true);
      // Auto-open the freshly created dossier in detail view
      const fresh = (await api.get(`/gestion-clients/${clientId}/travaux`)).data;
      const created = fresh.items?.find((i) => i.devis_id === r.data.devis_id);
      setData(fresh);
      if (created) {
        setSelectedItem(created);
        setSelectedStep('overview');
      }
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };

  const deleteDossier = async (item) => {
    if (!window.confirm(`Supprimer définitivement ce dossier travaux ?\nFiche, devis, chantier et factures liés seront supprimés.`)) return;
    setActingOn(item.devis_id || item.fiche_id);
    try {
      const params = new URLSearchParams();
      if (item.devis_id) params.append('devis_id', item.devis_id);
      if (item.fiche_id) params.append('fiche_id', item.fiche_id);
      await api.delete(`/travaux/dossier?${params.toString()}`);
      await load(true);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setActingOn(null); }
  };

  const markBcReceived = async (devisId, date) => {
    if (!devisId) return;
    setActingOn(devisId);
    try {
      const res = await api.post(`/travaux/devis/${devisId}/step-date`, { step: 'bon_commande', date: date || null, mark_done: true });
      if (res.data.chantier_id) alert(`✅ Chantier créé : ${res.data.chantier_id}`);
      await load(true);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setActingOn(null); }
  };
  const markDevisAccepte = async (devisId, date) => {
    if (!devisId) return;
    setActingOn(devisId);
    try { await api.post(`/travaux/devis/${devisId}/step-date`, { step: 'accepte', date: date || null, mark_done: true }); await load(true); }
    catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setActingOn(null); }
  };
  const markDevisEnvoye = async (devisId, date) => {
    if (!devisId) return;
    setActingOn(devisId);
    try { await api.post(`/travaux/devis/${devisId}/step-date`, { step: 'envoye', date: date || null, mark_done: true }); await load(true); }
    catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setActingOn(null); }
  };

  const updateStepDate = async (devisId, step, date, fiche_id) => {
    if (!devisId && step !== 'visite') return;
    setActingOn(devisId || fiche_id);
    try {
      await api.post(`/travaux/devis/${devisId || 'x'}/step-date`, { step, date: date || null, mark_done: false, fiche_id: fiche_id || null });
      await load(true);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setActingOn(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  if (selectedItem) {
    return (
      <ItemDetailView
        item={selectedItem}
        clientName={clientName || data?.client?.nom}
        selectedStep={selectedStep}
        setSelectedStep={setSelectedStep}
        onBack={closeItem}
        onMarkBc={markBcReceived}
        onMarkAccepte={markDevisAccepte}
        onMarkEnvoye={markDevisEnvoye}
        onUpdateStepDate={updateStepDate}
        acting={actingOn}
        reload={load}
      />
    );
  }

  const items = data?.items || [];
  const summary = data?.summary || {};

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="travaux-depannage-page">
      <div className="flex items-center gap-3 justify-between flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="travaux-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Wrench className="w-6 h-6 text-orange-600" /> Dossier En Cours
            </h1>
            <p className="text-sm text-slate-500">{clientName || data?.client?.nom}</p>
          </div>
        </div>
        <Button onClick={() => setShowCreate(true)}
          className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="new-dossier-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouveau dossier
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <SummaryCard label="Total dossiers"     value={summary.total ?? 0}            icon={ClipboardList} color="slate" />
        <SummaryCard label="Visites terrain"    value={summary.visites ?? 0}          icon={Calendar}      color="blue" />
        <SummaryCard label="Devis envoyés"      value={summary.devis_envoyes ?? 0}    icon={Send}          color="indigo" />
        <SummaryCard label="Devis signés"       value={summary.devis_acceptes ?? 0}   icon={CheckCircle2}  color="emerald" />
        <SummaryCard label="Chantiers en cours" value={summary.chantiers_lances ?? 0} icon={HardHat}       color="orange" />
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
          <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">Aucun dossier travaux pour le moment</h3>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it, idx) => (
            <div key={`${it.fiche_id || it.devis_id || idx}`}
              className="w-full bg-white rounded-xl border border-slate-200 hover:shadow-md hover:border-blue-300 transition-all overflow-hidden"
              data-testid={`travaux-card-${it.devis_id || it.fiche_id}`}>
              <div className="flex items-stretch">
                <button type="button" onClick={() => openItem(it)} className="flex-1 text-left p-5">
                  <CardSummary item={it} />
                </button>
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); deleteDossier(it); }}
                  disabled={actingOn === (it.devis_id || it.fiche_id)}
                  className="px-4 border-l border-slate-100 text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center"
                  title="Supprimer le dossier"
                  data-testid={`delete-dossier-${it.devis_id || it.fiche_id}`}>
                  {actingOn === (it.devis_id || it.fiche_id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create dossier dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="create-dossier-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-orange-600" /> Nouveau dossier travaux
              </h3>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Crée un dossier vierge pour <strong className="text-slate-700">{clientName || data?.client?.nom}</strong>. Tu pourras associer une fiche visite et ajouter les lignes du devis ensuite.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Titre du dossier</label>
                <Input value={createTitle} onChange={(e) => setCreateTitle(e.target.value)}
                  placeholder="ex: Travaux peinture stade Brestel"
                  data-testid="create-dossier-title" autoFocus />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
                <Button onClick={createDossier} disabled={creating} className="bg-orange-600 hover:bg-orange-700"
                  data-testid="create-dossier-confirm">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  Créer le dossier
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CardSummary({ item }) {
  const meta = STAGE_META[item.stage] || STAGE_META.visite;
  const dates = item.step_dates || {};
  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.color}`}>{meta.label}</span>
        <span className="text-xs text-slate-400">{fmtDate(item.date) || '-'}</span>
        <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
      </div>
      <h3 className="font-semibold text-slate-900 truncate">
        {item.project_title || (item.types_travaux?.length ? `Travaux : ${item.types_travaux.join(', ')}` : 'Dépannage / Travaux')}
      </h3>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
        {item.adresse && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" />{item.adresse}</span>}
        {item.signataires?.length > 0 && <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-slate-400" />{item.signataires.join(', ')}</span>}
        {item.devis_montant_ttc != null && <span className="inline-flex items-center gap-1.5 font-medium text-slate-900">{fmtMoney(item.devis_montant_ttc)} TTC</span>}
      </div>
      <div className="mt-3 flex items-center gap-1 flex-wrap">
        {STEPS.filter(s => s.key !== 'overview').map((s) => {
          const reached = !!dates[s.key];
          return (
            <div key={s.key} className="flex items-center gap-1">
              <div className={`w-2.5 h-2.5 rounded-full ${reached ? 'bg-blue-600' : 'bg-slate-200'}`} />
              <span className={`text-[10px] ${reached ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>{s.label}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ==========================================
// DETAIL VIEW with stepper
// ==========================================
function ItemDetailView({ item, clientName, selectedStep, setSelectedStep, onBack, onMarkBc, onMarkAccepte, onMarkEnvoye, onUpdateStepDate, acting, reload }) {
  const dates = item.step_dates || {};
  const reachedStepIdx = (() => {
    let last = 0; // overview always considered reached
    STEPS.forEach((s, i) => { if (s.key !== 'overview' && dates[s.key]) last = i; });
    return last;
  })();

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="detail-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-500">{clientName}</p>
          <h1 className="text-xl font-bold text-slate-900 truncate">
            {item.project_title || (item.types_travaux?.length ? `Travaux : ${item.types_travaux.join(', ')}` : 'Dossier travaux')}
          </h1>
        </div>
        {item.devis_id && <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-1 rounded">{item.devis_id}</span>}
      </div>

      {/* Stepper */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-stretch overflow-x-auto scrollbar-hide">
          {STEPS.map((s, i) => {
            const reached = s.key === 'overview' ? true : !!dates[s.key];
            const active = selectedStep === s.key;
            const Icon = s.icon;
            const colorClasses = {
              slate:   { bg: 'bg-slate-700',   text: 'text-slate-700',   ring: 'ring-slate-200' },
              blue:    { bg: 'bg-blue-600',    text: 'text-blue-700',    ring: 'ring-blue-200' },
              indigo:  { bg: 'bg-indigo-600',  text: 'text-indigo-700',  ring: 'ring-indigo-200' },
              emerald: { bg: 'bg-emerald-600', text: 'text-emerald-700', ring: 'ring-emerald-200' },
              amber:   { bg: 'bg-amber-600',   text: 'text-amber-700',   ring: 'ring-amber-200' },
              orange:  { bg: 'bg-orange-600',  text: 'text-orange-700',  ring: 'ring-orange-200' },
            };
            const c = colorClasses[s.color] || colorClasses.blue;
            return (
              <React.Fragment key={s.key}>
                <button type="button" onClick={() => setSelectedStep(s.key)}
                  className="flex flex-col items-center gap-1.5 px-2 sm:px-3 py-2 transition-all min-w-[80px] flex-1"
                  data-testid={`step-${s.key}`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ring-4 ${
                    active ? `${c.bg} ${c.ring} text-white scale-105` :
                    reached ? `${c.bg} ring-transparent text-white` :
                    'bg-slate-100 ring-transparent text-slate-400'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-medium text-center ${active ? c.text : reached ? 'text-slate-700' : 'text-slate-400'}`}>{s.label}</span>
                  <span className={`text-[10px] ${reached ? 'text-slate-500' : 'text-slate-300'}`}>
                    {s.key === 'overview' ? '' : (fmtDate(dates[s.key]) || '—')}
                  </span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className="flex items-center pt-6 px-1">
                    <div className={`h-0.5 w-3 sm:w-6 ${i < reachedStepIdx ? 'bg-blue-600' : 'bg-slate-200'}`} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step content */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {selectedStep === 'overview'     && <StepOverview item={item} reload={reload} />}
        {selectedStep === 'visite'       && <StepVisite item={item} reload={reload} onUpdateStepDate={onUpdateStepDate} acting={acting} />}
        {selectedStep === 'devis'        && <StepDevis item={item} reload={reload} onUpdateStepDate={onUpdateStepDate} acting={acting} />}
        {selectedStep === 'envoye'       && <StepEnvoye item={item} acting={acting} onMarkEnvoye={onMarkEnvoye} onUpdateStepDate={onUpdateStepDate} />}
        {selectedStep === 'accepte'      && <StepAccepte item={item} acting={acting} onMarkAccepte={onMarkAccepte} onUpdateStepDate={onUpdateStepDate} />}
        {selectedStep === 'bon_commande' && <StepBonCommande item={item} acting={acting} onMarkBc={onMarkBc} onUpdateStepDate={onUpdateStepDate} />}
        {selectedStep === 'chantier'     && <StepChantier item={item} reload={reload} onUpdateStepDate={onUpdateStepDate} acting={acting} />}
      </div>
    </div>
  );
}

function StepHeader({ icon: Icon, title, subtitle, color = 'blue' }) {
  const colors = { slate:'bg-slate-100 text-slate-700', blue:'bg-blue-50 text-blue-700', indigo:'bg-indigo-50 text-indigo-700', emerald:'bg-emerald-50 text-emerald-700', amber:'bg-amber-50 text-amber-700', orange:'bg-orange-50 text-orange-700' };
  return (
    <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-100">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color] || colors.blue}`}><Icon className="w-6 h-6" /></div>
      <div><h2 className="text-lg font-bold text-slate-900">{title}</h2>{subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}</div>
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-baseline gap-3 py-2 border-b border-slate-50 last:border-0">
      <span className="text-xs text-slate-500 w-32 shrink-0">{label}</span>
      <span className={`text-sm text-slate-900 ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

// ==========================================
// STEP: VUE D'ENSEMBLE (key figures + costs + suppliers + factures)
// ==========================================
function StepOverview({ item, reload }) {
  const [lifecycle, setLifecycle] = useState(null);
  const [sousTraitants, setSousTraitants] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [form, setForm] = useState({
    sous_traitant_id: '', sous_traitant_nom: '',
    cout_main_oeuvre: '', cout_materiaux: '', cout_autres: '',
    notes_chantier: '',
  });
  const [articles, setArticles] = useState([]);

  const reloadLifecycle = async () => {
    try {
      const r = await api.get(`/devis/${item.devis_id}/lifecycle`);
      setLifecycle(r.data);
      const lc = r.data.lifecycle || {};
      setForm({
        sous_traitant_id: lc.sous_traitant_id || '',
        sous_traitant_nom: lc.sous_traitant_nom || '',
        cout_main_oeuvre: lc.cout_main_oeuvre ?? '',
        cout_materiaux: lc.cout_materiaux ?? '',
        cout_autres: lc.cout_autres ?? '',
        notes_chantier: lc.notes_chantier || '',
      });
      setArticles(lc.fournisseurs_articles || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (!item.devis_id) return;
    reloadLifecycle();
    (async () => {
      try {
        const r2 = await api.get('/sous-traitants-selector');
        setSousTraitants(r2.data.sous_traitants || []);
      } catch (e) { console.error(e); }
    })();
  }, [item.devis_id]);

  const saveCosts = async () => {
    setSaving(true);
    try {
      await api.patch(`/devis/${item.devis_id}/lifecycle`, {
        sous_traitant_id: form.sous_traitant_id,
        sous_traitant_nom: form.sous_traitant_nom,
        cout_main_oeuvre: parseFloat(form.cout_main_oeuvre) || 0,
        cout_materiaux: parseFloat(form.cout_materiaux) || 0,
        cout_autres: parseFloat(form.cout_autres) || 0,
        notes_chantier: form.notes_chantier,
      });
      await reloadLifecycle();
      setEditing(false);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const saveArticles = async (newArticles) => {
    try {
      await api.patch(`/devis/${item.devis_id}/lifecycle`, { fournisseurs_articles: newArticles });
      setArticles(newArticles);
      await reloadLifecycle();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  if (!item.devis_id) {
    return (
      <div>
        <StepHeader icon={Layers} title="Vue d'ensemble" color="slate" />
        <div className="text-center py-8 text-slate-500">
          Aucun devis associé à ce dossier — pas de vue financière disponible.
        </div>
      </div>
    );
  }

  const fin = lifecycle?.financials || {};
  const rent = lifecycle?.rentabilite || {};
  const badge = rent.badge_color || 'neutral';
  const BADGE_STYLES = {
    green:   { box: 'bg-emerald-50 border-emerald-300', text: 'text-emerald-700', icon: ShieldCheck,    pillBg: 'bg-emerald-600' },
    orange:  { box: 'bg-amber-50 border-amber-300',     text: 'text-amber-700',   icon: AlertTriangle,  pillBg: 'bg-amber-500' },
    red:     { box: 'bg-red-50 border-red-300',         text: 'text-red-700',     icon: AlertTriangle,  pillBg: 'bg-red-600' },
    neutral: { box: 'bg-slate-50 border-slate-200',     text: 'text-slate-600',   icon: Settings,       pillBg: 'bg-slate-400' },
  };
  const bs = BADGE_STYLES[badge];
  const BadgeIcon = bs.icon;

  return (
    <div className="space-y-6">
      <StepHeader icon={Layers} title="Vue d'ensemble" subtitle={`${item.project_title || 'Dossier'} · ${item.devis_id}`} color="slate" />

      {/* Key figures (5 cards) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Devis HT"        value={fmtMoney(fin.montant_devis_ht)} icon={FileText}  color="slate" />
        <KPI label="Main d'œuvre S/T" value={fmtMoney(fin.cout_main_oeuvre)} icon={Briefcase} color="orange" />
        <KPI label="Matériaux"        value={fmtMoney(fin.cout_materiaux)}   icon={Package}   color="blue" />
        <KPI label="Coût total réel"  value={fmtMoney(fin.cout_total_reel)}  icon={Euro}      color="red" />
        <KPI label="Marge nette"
             value={`${fmtMoney(fin.marge_nette)}${fin.marge_percent != null ? ` (${fin.marge_percent}%)` : ''}`}
             icon={TrendingUp} color={(fin.marge_nette ?? 0) >= 0 ? 'emerald' : 'red'} highlight />
      </div>

      {/* Rentabilité — badge de décision (avec quote-part charges fixes) */}
      <div className={`rounded-xl border-2 ${bs.box} p-4`} data-testid="rentabilite-card">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${bs.pillBg} text-white shrink-0`}>
            <BadgeIcon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Décision rentabilité</p>
                <p className={`text-lg font-bold ${bs.text}`} data-testid="rentabilite-badge">{rent.badge_label || '—'}</p>
              </div>
              <button onClick={() => setShowSettings(true)}
                className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                data-testid="open-finance-settings-btn">
                <Settings className="w-3.5 h-3.5" /> Paramètres
              </button>
            </div>
            <p className="text-sm text-slate-700 mt-1.5">{rent.message}</p>
            {rent.coef_charges_pourcent != null && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-slate-200/60">
                <MiniStat label="Quote-part charges fixes" value={fmtMoney(rent.quote_part_charges_fixes)} hint={`${rent.coef_charges_pourcent}% du HT`} />
                <MiniStat label="Coût total + charges"      value={fmtMoney(rent.cout_total_avec_charges)} />
                <MiniStat label="Marge nette finale"        value={fmtMoney(rent.marge_nette_finale)} hint={`${rent.marge_percent_finale ?? 0}%`} highlight={badge} />
                <MiniStat label="Cible / Seuil"             value={`${rent.marge_cible}% / ${rent.marge_seuil_orange}%`} hint="objectif / plancher" />
              </div>
            )}
          </div>
        </div>
      </div>

      {showSettings && <FinanceSettingsDialog onClose={() => setShowSettings(false)} onSaved={reloadLifecycle} />}

      {/* Sous-traitant + costs */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
            <Truck className="w-4 h-4 text-orange-600" /> Sous-traitant & coûts réels
          </h3>
          {!editing ? (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-costs-btn">
              <Edit className="w-3.5 h-3.5 mr-1" /> Modifier
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Annuler</Button>
              <Button size="sm" onClick={saveCosts} disabled={saving} data-testid="save-costs-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Enregistrer
              </Button>
            </div>
          )}
        </div>

        <div className="p-5 space-y-3">
          {!editing ? (
            <>
              <InfoRow label="Sous-traitant"   value={form.sous_traitant_nom || '—'} />
              <InfoRow label="Main d'œuvre"    value={fmtMoney(parseFloat(form.cout_main_oeuvre) || 0)} />
              <InfoRow label="Matériaux"        value={fmtMoney(parseFloat(form.cout_materiaux) || 0)} />
              <InfoRow label="Autres frais"     value={fmtMoney(parseFloat(form.cout_autres) || 0)} />
              {form.notes_chantier && <InfoRow label="Notes" value={form.notes_chantier} />}
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Sous-traitant</label>
                <select className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  value={form.sous_traitant_id}
                  onChange={(e) => {
                    const st = sousTraitants.find((s) => s.sous_traitant_id === e.target.value);
                    setForm({ ...form, sous_traitant_id: e.target.value, sous_traitant_nom: st?.nom || '' });
                  }}>
                  <option value="">— aucun —</option>
                  {sousTraitants.map((st) => (
                    <option key={st.sous_traitant_id} value={st.sous_traitant_id}>{st.nom}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <NumberField label="Main d'œuvre (€)" value={form.cout_main_oeuvre} onChange={(v) => setForm({ ...form, cout_main_oeuvre: v })} testid="cost-mo" />
                <NumberField label="Matériaux (€)"     value={form.cout_materiaux}    onChange={(v) => setForm({ ...form, cout_materiaux: v })}    testid="cost-mat" />
                <NumberField label="Autres (€)"        value={form.cout_autres}       onChange={(v) => setForm({ ...form, cout_autres: v })}       testid="cost-aut" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                <Textarea rows={2} value={form.notes_chantier} onChange={(e) => setForm({ ...form, notes_chantier: e.target.value })} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Fournisseurs & articles */}
      <FournisseursArticlesEditor articles={articles} onSave={saveArticles} />

      {/* Factures du chantier */}
      {item.chantier_id && <FacturesChantier chantierId={item.chantier_id} />}
    </div>
  );
}

function KPI({ label, value, icon: Icon, color, highlight }) {
  const colors = {
    slate:'bg-slate-100 text-slate-700',
    orange:'bg-orange-100 text-orange-700',
    blue:'bg-blue-100 text-blue-700',
    red:'bg-red-100 text-red-700',
    emerald:'bg-emerald-100 text-emerald-700',
  };
  return (
    <div className={`rounded-xl p-3 border ${highlight ? `${colors[color]} border-2` : 'bg-white border-slate-200'}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded-md ${colors[color]}`}><Icon className="w-3.5 h-3.5" /></div>
        <span className="text-[11px] text-slate-500">{label}</span>
      </div>
      <p className="text-base sm:text-lg font-bold text-slate-900 leading-tight">{value}</p>
    </div>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <Input type="number" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0" data-testid={testid} />
    </div>
  );
}

function MiniStat({ label, value, hint, highlight }) {
  const colors = {
    green:'text-emerald-700', orange:'text-amber-700', red:'text-red-700', neutral:'text-slate-700',
  };
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-sm font-bold ${colors[highlight] || 'text-slate-900'}`}>{value}</p>
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}

function FinanceSettingsDialog({ onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/company/finance').then((r) => setData(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await api.put('/company/finance', {
        charges_fixes_annuelles: parseFloat(data.charges_fixes_annuelles) || 0,
        ca_objectif_annuel: parseFloat(data.ca_objectif_annuel) || 0,
        marge_nette_cible: parseFloat(data.marge_nette_cible) || 0,
        marge_nette_seuil_orange: parseFloat(data.marge_nette_seuil_orange) || 0,
      });
      onSaved && onSaved();
      onClose();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="finance-settings-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Settings className="w-5 h-5 text-slate-700" /> Paramètres financiers
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {!data ? (
          <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Ces paramètres servent à calculer la marge nette finale (avec quote-part de charges fixes) et le badge de décision sur chaque devis.</p>

            <NumberField label="Charges fixes annuelles (€)" value={data.charges_fixes_annuelles} onChange={(v) => setData({ ...data, charges_fixes_annuelles: v })} testid="settings-charges" />
            <NumberField label="CA objectif annuel (€)"      value={data.ca_objectif_annuel}      onChange={(v) => setData({ ...data, ca_objectif_annuel: v })}      testid="settings-ca" />

            <div className="grid grid-cols-2 gap-3">
              <NumberField label="Marge nette cible (%)"        value={data.marge_nette_cible}        onChange={(v) => setData({ ...data, marge_nette_cible: v })}        testid="settings-cible" />
              <NumberField label="Seuil orange (%)"             value={data.marge_nette_seuil_orange} onChange={(v) => setData({ ...data, marge_nette_seuil_orange: v })} testid="settings-orange" />
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1">
              <div className="flex justify-between"><span>Coef charges fixes calculé</span><strong className="text-slate-900">{data.coef_charges_pourcent}%</strong></div>
              <div className="flex justify-between"><span>Coef vente plancher</span><strong className="text-slate-900">×{data.coef_vente_plancher ?? '—'}</strong></div>
              <p className="text-[11px] text-slate-500 mt-2 pt-2 border-t border-slate-200">
                <strong>Décision visuelle</strong> : <span className="text-emerald-700">vert</span> si marge ≥ cible, <span className="text-amber-700">orange</span> entre seuil et cible, <span className="text-red-700">rouge</span> sous le seuil.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Enregistrer
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================
// FOURNISSEURS & ARTICLES (in Overview)
// ==========================================
function FournisseursArticlesEditor({ articles, onSave }) {
  const [items, setItems] = useState(articles || []);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ fournisseur: '', reference: '', designation: '', quantite: '', prix_unitaire: '' });

  useEffect(() => { setItems(articles || []); }, [articles]);

  const total = items.reduce((s, a) => s + (parseFloat(a.montant) || 0), 0);

  const addItem = () => {
    if (!draft.fournisseur && !draft.reference && !draft.designation) return;
    const qte = parseFloat(draft.quantite) || 1;
    const pu = parseFloat(draft.prix_unitaire) || 0;
    const newItem = { ...draft, quantite: qte, prix_unitaire: pu, montant: +(qte * pu).toFixed(2) };
    const next = [...items, newItem];
    setItems(next);
    onSave(next);
    setDraft({ fournisseur: '', reference: '', designation: '', quantite: '', prix_unitaire: '' });
    setAdding(false);
  };

  const remove = (idx) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    onSave(next);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
          <Package className="w-4 h-4 text-blue-600" /> Fournisseurs & articles
          <span className="text-xs font-normal text-slate-500">({items.length} {items.length > 1 ? 'lignes' : 'ligne'} · {fmtMoney(total)})</span>
        </h3>
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} disabled={adding} data-testid="add-article-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
        </Button>
      </div>

      {(items.length === 0 && !adding) ? (
        <div className="p-6 text-center text-sm text-slate-400">Aucun fournisseur enregistré pour ce devis.</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="w-full text-xs" role="table">
            <div role="row" className="grid grid-cols-[1.4fr_1fr_2fr_60px_80px_100px_40px] bg-slate-50 text-left">
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600">Fournisseur</div>
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600">Référence</div>
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600">Désignation</div>
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600 text-right">Qté</div>
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600 text-right">PU</div>
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600 text-right">Total</div>
              <div role="columnheader"></div>
            </div>
            <div role="rowgroup">
              {items.map((a, i) => (
                <div key={i} role="row" className="grid grid-cols-[1.4fr_1fr_2fr_60px_80px_100px_40px] items-center border-t border-slate-100">
                  <div role="cell" className="px-3 py-2 text-slate-900">{a.fournisseur || '—'}</div>
                  <div role="cell" className="px-3 py-2 font-mono text-slate-600">{a.reference || '—'}</div>
                  <div role="cell" className="px-3 py-2 text-slate-700">{a.designation || '—'}</div>
                  <div role="cell" className="px-3 py-2 text-right text-slate-600">{a.quantite}</div>
                  <div role="cell" className="px-3 py-2 text-right text-slate-600">{fmtMoney(a.prix_unitaire)}</div>
                  <div role="cell" className="px-3 py-2 text-right font-medium text-slate-900">{fmtMoney(a.montant)}</div>
                  <div role="cell" className="px-2 py-2">
                    <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-600" data-testid={`del-article-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              {adding && (
                <div role="row" className="grid grid-cols-[1.4fr_1fr_2fr_60px_80px_100px_40px] items-center border-t border-slate-100 bg-blue-50/30">
                  <div role="cell" className="px-2 py-2"><Input size="sm" value={draft.fournisseur} onChange={(e) => setDraft({ ...draft, fournisseur: e.target.value })} placeholder="ex: Point.P" data-testid="draft-fournisseur" /></div>
                  <div role="cell" className="px-2 py-2"><Input size="sm" value={draft.reference} onChange={(e) => setDraft({ ...draft, reference: e.target.value })} placeholder="ex: PRT-001" /></div>
                  <div role="cell" className="px-2 py-2"><Input size="sm" value={draft.designation} onChange={(e) => setDraft({ ...draft, designation: e.target.value })} placeholder="Désignation" /></div>
                  <div role="cell" className="px-2 py-2"><Input size="sm" type="number" inputMode="decimal" value={draft.quantite} onChange={(e) => setDraft({ ...draft, quantite: e.target.value })} placeholder="1" /></div>
                  <div role="cell" className="px-2 py-2"><Input size="sm" type="number" inputMode="decimal" value={draft.prix_unitaire} onChange={(e) => setDraft({ ...draft, prix_unitaire: e.target.value })} placeholder="0" /></div>
                  <div role="cell" className="px-2 py-2 text-right text-xs text-slate-500">{fmtMoney((parseFloat(draft.quantite) || 0) * (parseFloat(draft.prix_unitaire) || 0))}</div>
                  <div role="cell" className="px-1 py-2">
                    <div className="flex flex-col gap-1">
                      <button onClick={addItem} className="text-blue-600" data-testid="confirm-article-btn"><CheckCircle2 className="w-4 h-4" /></button>
                      <button onClick={() => { setAdding(false); setDraft({ fournisseur: '', reference: '', designation: '', quantite: '', prix_unitaire: '' }); }} className="text-slate-400"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// FACTURES DU CHANTIER (in Overview)
// ==========================================
function FacturesChantier({ chantierId }) {
  const [factures, setFactures] = useState([]);
  const [totals, setTotals] = useState({ total_ht: 0, total_ttc: 0 });
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ type: 'matériaux', fournisseur: '', numero: '', date: '', montant_ht: '', montant_ttc: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/chantiers/${chantierId}/factures`);
      setFactures(r.data.factures || []);
      setTotals({ total_ht: r.data.total_ht || 0, total_ttc: r.data.total_ttc || 0 });
    } catch (e) { console.error(e); }
  };
  useEffect(() => { if (chantierId) load(); }, [chantierId]);

  const save = async () => {
    if (!draft.fournisseur && !draft.numero) { alert('Indiquez au moins le fournisseur ou le numéro'); return; }
    setSaving(true);
    try {
      await api.post(`/chantiers/${chantierId}/factures`, {
        ...draft,
        montant_ht: parseFloat(draft.montant_ht) || 0,
        montant_ttc: parseFloat(draft.montant_ttc) || 0,
      });
      setDraft({ type: 'matériaux', fournisseur: '', numero: '', date: '', montant_ht: '', montant_ttc: '' });
      setAdding(false);
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await api.delete(`/chantiers/${chantierId}/factures/${id}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const TYPE_BADGE = {
    'matériaux':       'bg-blue-100 text-blue-700',
    'sous-traitant':   'bg-orange-100 text-orange-700',
    'autre':           'bg-slate-100 text-slate-700',
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
          <Receipt className="w-4 h-4 text-purple-600" /> Factures du chantier
          <span className="text-xs font-normal text-slate-500">
            ({factures.length} · {fmtMoney(totals.total_ttc)} TTC)
          </span>
        </h3>
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} disabled={adding} data-testid="add-facture-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une facture
        </Button>
      </div>

      {adding && (
        <div className="p-4 bg-purple-50/30 border-b border-slate-100 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
              <select className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })}>
                <option value="matériaux">Matériaux</option>
                <option value="sous-traitant">Sous-traitant</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Fournisseur / Sous-traitant</label>
              <Input value={draft.fournisseur} onChange={(e) => setDraft({ ...draft, fournisseur: e.target.value })} placeholder="ex: Point.P" data-testid="facture-fournisseur" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">N° facture</label>
              <Input value={draft.numero} onChange={(e) => setDraft({ ...draft, numero: e.target.value })} placeholder="ex: FACT-2026-001" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
              <Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
            </div>
            <NumberField label="Montant HT (€)"  value={draft.montant_ht}  onChange={(v) => setDraft({ ...draft, montant_ht: v })} testid="facture-ht" />
            <NumberField label="Montant TTC (€)" value={draft.montant_ttc} onChange={(v) => setDraft({ ...draft, montant_ttc: v })} testid="facture-ttc" />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Annuler</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="save-facture-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Enregistrer
            </Button>
          </div>
        </div>
      )}

      {factures.length === 0 && !adding ? (
        <div className="p-6 text-center text-sm text-slate-400">Aucune facture enregistrée pour ce chantier.</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="w-full text-xs" role="table">
            <div role="row" className="grid grid-cols-[1fr_1.4fr_1fr_1fr_1fr_1fr_40px] bg-slate-50 text-left">
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600">Type</div>
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600">Fournisseur</div>
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600">N°</div>
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600">Date</div>
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600 text-right">HT</div>
              <div role="columnheader" className="px-3 py-2 font-medium text-slate-600 text-right">TTC</div>
              <div role="columnheader"></div>
            </div>
            <div role="rowgroup">
              {factures.map((f) => (
                <div key={f.facture_id} role="row" className="grid grid-cols-[1fr_1.4fr_1fr_1fr_1fr_1fr_40px] items-center border-t border-slate-100">
                  <div role="cell" className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${TYPE_BADGE[f.type] || TYPE_BADGE.autre}`}>{f.type}</span></div>
                  <div role="cell" className="px-3 py-2 text-slate-900">{f.fournisseur || '—'}</div>
                  <div role="cell" className="px-3 py-2 font-mono text-slate-600">{f.numero || '—'}</div>
                  <div role="cell" className="px-3 py-2 text-slate-600">{fmtDate(f.date) || '—'}</div>
                  <div role="cell" className="px-3 py-2 text-right text-slate-700">{fmtMoney(f.montant_ht)}</div>
                  <div role="cell" className="px-3 py-2 text-right font-medium text-slate-900">{fmtMoney(f.montant_ttc)}</div>
                  <div role="cell" className="px-2 py-2">
                    <button onClick={() => remove(f.facture_id)} className="text-slate-400 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// STEP: VISITE (with associate/dissociate devis)
// ==========================================
function StepVisite({ item, reload, onUpdateStepDate, acting }) {
  const [associating, setAssociating] = useState(false);
  const [available, setAvailable] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [pickedDate, setPickedDate] = useState(toYmd(item.step_dates?.visite) || toYmd(new Date().toISOString()));

  const openAssociator = async () => {
    setAssociating(true);
    setLoading(true);
    try {
      const r = await api.get('/travaux/devis-disponibles');
      setAvailable(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const associate = async (devisId) => {
    if (!item.fiche_id) return;
    try {
      await api.post(`/devis/${devisId}/associate-fiche`, { fiche_id: item.fiche_id });
      setAssociating(false);
      await reload();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const dissociate = async () => {
    if (!item.devis_id || !window.confirm('Dissocier le devis de cette fiche visite ?')) return;
    try {
      await api.post(`/devis/${item.devis_id}/associate-fiche`, { fiche_id: null });
      await reload();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const filteredAvailable = available.filter((d) =>
    !search ||
    (d.devis_id || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.project_title || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.client_nom || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!item.fiche_id) {
    return (
      <div>
        <StepHeader icon={ClipboardList} title="Visite terrain" color="slate" />
        <div className="text-center py-8 text-slate-500">
          <p>Aucune fiche visite n'est rattachée à ce dossier.</p>
          <p className="text-xs mt-1">Le devis a été créé directement, sans visite préalable.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <StepHeader icon={ClipboardList} title="Visite terrain" subtitle={`Réalisée le ${fmtDate(item.step_dates?.visite) || '—'}`} color="blue" />
      <div className="mb-4">
        <StepDateEditor label="Modifier la date de visite" value={pickedDate} setValue={setPickedDate}
          onSave={() => onUpdateStepDate(null, 'visite', pickedDate, item.fiche_id)}
          acting={acting} disabled={!item.fiche_id} testid="edit-date-visite" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <InfoRow label="Fiche" value={item.fiche_id} mono />
          <InfoRow label="Adresse" value={item.adresse} />
          <InfoRow label="Signataire(s)" value={item.signataires?.join(', ')} />
          <InfoRow label="État support" value={item.etat_support} />
        </div>
        <div>
          <InfoRow label="Types de travaux" value={item.types_travaux?.join(', ')} />
          <InfoRow label="Contraintes" value={item.contraintes?.join(', ')} />
          <InfoRow label="Statut fiche" value={item.statut_fiche} />
        </div>
      </div>
      {item.remarques && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg">
          <p className="text-xs font-medium text-amber-900 mb-1">Remarques terrain</p>
          <p className="text-sm text-amber-800 italic">"{item.remarques}"</p>
        </div>
      )}
      {item.photos?.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-medium text-slate-700 mb-2 flex items-center gap-1.5">
            <Camera className="w-3.5 h-3.5" /> {item.photos.length} photo{item.photos.length > 1 ? 's' : ''}
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {item.photos.map((p, i) => {
              const src = p.url ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : null;
              return (
                <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="block rounded-lg overflow-hidden border border-slate-200 hover:border-blue-400 transition">
                  {src ? <img src={src} alt={p.filename} className="w-full h-24 object-cover" loading="lazy" /> : <div className="w-full h-24 bg-slate-100 flex items-center justify-center"><ImageIcon className="w-5 h-5 text-slate-400"/></div>}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Associate / dissociate devis */}
      <div className="mt-6 pt-5 border-t border-slate-100">
        <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Link2 className="w-4 h-4 text-blue-600" /> Devis associé
        </h4>
        {item.devis_id ? (
          <div className="flex items-center justify-between gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-blue-900 font-mono">{item.devis_id}</p>
              <p className="text-xs text-blue-700 truncate">{item.project_title || '—'} · {fmtMoney(item.devis_montant_ttc)} TTC</p>
            </div>
            <Button size="sm" variant="outline" onClick={dissociate} data-testid="dissociate-devis-btn">
              <Unlink className="w-3.5 h-3.5 mr-1" /> Dissocier
            </Button>
          </div>
        ) : associating ? (
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input className="pl-10" placeholder="Rechercher un devis…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="border border-slate-200 rounded-lg max-h-72 overflow-y-auto">
              {loading ? (
                <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
              ) : filteredAvailable.length === 0 ? (
                <div className="text-center py-4 text-xs text-slate-400">Aucun devis disponible</div>
              ) : (
                filteredAvailable.slice(0, 30).map((d) => (
                  <button key={d.devis_id + d.date_creation} onClick={() => associate(d.devis_id)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-50 last:border-0">
                    <p className="text-sm font-medium text-slate-900">{d.project_title || d.devis_id}</p>
                    <p className="text-xs text-slate-500">{d.client_nom} · {fmtMoney(d.montant_ttc)} · {d.statut}</p>
                  </button>
                ))
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => setAssociating(false)}>Fermer</Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={openAssociator} data-testid="open-associate-devis">
            <Link2 className="w-3.5 h-3.5 mr-1" /> Associer un devis existant
          </Button>
        )}
      </div>
    </div>
  );
}

// ==========================================
// STEP: DEVIS (with associate/dissociate fiche)
// ==========================================
function StepDevis({ item, reload, onUpdateStepDate, acting }) {
  const [associating, setAssociating] = useState(false);
  const [available, setAvailable] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [pickedDate, setPickedDate] = useState(toYmd(item.step_dates?.devis) || toYmd(new Date().toISOString()));

  const openAssociator = async () => {
    setAssociating(true);
    setLoading(true);
    try {
      const r = await api.get('/travaux/fiches-disponibles');
      setAvailable(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const associate = async (ficheId) => {
    if (!item.devis_id) return;
    try {
      await api.post(`/devis/${item.devis_id}/associate-fiche`, { fiche_id: ficheId });
      setAssociating(false);
      await reload();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const dissociate = async () => {
    if (!item.devis_id || !window.confirm('Dissocier la fiche visite de ce devis ?')) return;
    try {
      await api.post(`/devis/${item.devis_id}/associate-fiche`, { fiche_id: null });
      await reload();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const filteredAvailable = available.filter((f) =>
    !search ||
    (f.fiche_id || '').toLowerCase().includes(search.toLowerCase()) ||
    (f.client_nom || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!item.devis_id) {
    return (
      <div>
        <StepHeader icon={FileText} title="Devis" color="slate" />
        <div className="text-center py-8 text-slate-500">Aucun devis généré pour le moment.</div>
      </div>
    );
  }
  const totaux = item.totaux || {};
  return (
    <div>
      <StepHeader icon={FileText} title="Devis" subtitle={`Créé le ${fmtDate(item.step_dates?.devis) || '—'}`} color="blue" />
      <div className="mb-4">
        <StepDateEditor label="Modifier la date du devis" value={pickedDate} setValue={setPickedDate}
          onSave={() => onUpdateStepDate(item.devis_id, 'devis', pickedDate)}
          acting={acting} disabled={!item.devis_id} testid="edit-date-devis" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <InfoRow label="Numéro" value={item.devis_id} mono />
          <InfoRow label="Titre projet" value={item.project_title} />
          <InfoRow label="Statut" value={item.devis_statut} />
        </div>
        <div>
          <InfoRow label="Total HT" value={fmtMoney(totaux.total_ht || totaux.ht || item.devis_montant_ht)} />
          <InfoRow label="TVA" value={fmtMoney(totaux.tva_montant || totaux.tva)} />
          <InfoRow label="Total TTC" value={<strong>{fmtMoney(totaux.total_ttc || totaux.ttc || item.devis_montant_ttc)}</strong>} />
        </div>
      </div>
      {item.lignes?.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-medium text-slate-700 mb-2">{item.lignes.length} ligne{item.lignes.length > 1 ? 's' : ''} de devis</p>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="w-full text-xs" role="table">
              <div role="row" className="grid grid-cols-[1fr_60px_80px_100px] bg-slate-50">
                <div role="columnheader" className="text-left px-3 py-2 font-medium text-slate-600">Description</div>
                <div role="columnheader" className="text-right px-3 py-2 font-medium text-slate-600">Qté</div>
                <div role="columnheader" className="text-right px-3 py-2 font-medium text-slate-600">PU</div>
                <div role="columnheader" className="text-right px-3 py-2 font-medium text-slate-600">Total HT</div>
              </div>
              <div role="rowgroup">
                {item.lignes.slice(0, 15).map((l, i) => (
                  <div key={i} role="row" className="grid grid-cols-[1fr_60px_80px_100px] items-center border-t border-slate-100">
                    <div role="cell" className="px-3 py-2 text-slate-700">{l.description}</div>
                    <div role="cell" className="text-right px-3 py-2 text-slate-600">{l.quantite} {l.unite}</div>
                    <div role="cell" className="text-right px-3 py-2 text-slate-600">{fmtMoney(l.prix_unitaire)}</div>
                    <div role="cell" className="text-right px-3 py-2 font-medium text-slate-900">{fmtMoney(l.total_ht || (l.quantite * l.prix_unitaire))}</div>
                  </div>
                ))}
                {item.lignes.length > 15 && <div role="row" className="text-center py-2 text-xs text-slate-400">… et {item.lignes.length - 15} autres lignes</div>}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mt-5">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/devis/${item.devis_id}/pdf`} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-sm font-medium" data-testid="step-devis-pdf-btn">
          <FileText className="w-4 h-4" /> Télécharger le PDF
        </a>
      </div>

      {/* Associate / dissociate fiche visite */}
      <div className="mt-6 pt-5 border-t border-slate-100">
        <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Link2 className="w-4 h-4 text-blue-600" /> Fiche visite associée
        </h4>
        {item.fiche_id ? (
          <div className="flex items-center justify-between gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-blue-900 font-mono">{item.fiche_id}</p>
              <p className="text-xs text-blue-700 truncate">{item.types_travaux?.join(', ') || '—'}</p>
            </div>
            <Button size="sm" variant="outline" onClick={dissociate} data-testid="dissociate-fiche-btn">
              <Unlink className="w-3.5 h-3.5 mr-1" /> Dissocier
            </Button>
          </div>
        ) : associating ? (
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input className="pl-10" placeholder="Rechercher une fiche visite…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="border border-slate-200 rounded-lg max-h-72 overflow-y-auto">
              {loading ? <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin inline" /></div> :
                filteredAvailable.length === 0 ? <div className="text-center py-4 text-xs text-slate-400">Aucune fiche disponible</div> :
                filteredAvailable.slice(0, 30).map((f) => (
                  <button key={f.fiche_id} onClick={() => associate(f.fiche_id)} className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-50 last:border-0">
                    <p className="text-sm font-medium text-slate-900">{f.client_nom || f.fiche_id}</p>
                    <p className="text-xs text-slate-500">{f.types_travaux?.join(', ')} · {fmtDate(f.created_at)}</p>
                  </button>
                ))
              }
            </div>
            <Button size="sm" variant="outline" onClick={() => setAssociating(false)}>Fermer</Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={openAssociator} data-testid="open-associate-fiche">
            <Link2 className="w-3.5 h-3.5 mr-1" /> Associer une fiche visite
          </Button>
        )}
      </div>
    </div>
  );
}

// ==========================================
// STEP: ENVOYÉ
// ==========================================
function StepEnvoye({ item, acting, onMarkEnvoye, onUpdateStepDate }) {
  const date = item.step_dates?.envoye;
  const [picked, setPicked] = useState(toYmd(date) || toYmd(new Date().toISOString()));
  return (
    <div>
      <StepHeader icon={Send} title="Devis envoyé" subtitle={date ? `Envoyé le ${fmtDate(date)}` : 'Pas encore envoyé'} color="indigo" />
      {date ? (
        <div className="space-y-3">
          <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-lg">
            <p className="text-sm text-indigo-900">Le devis a été envoyé au client le <strong>{fmtDate(date)}</strong>.</p>
            <p className="text-xs text-indigo-700 mt-1">En attente de retour de la collectivité.</p>
          </div>
          <StepDateEditor label="Modifier la date d'envoi" value={picked} setValue={setPicked}
            onSave={() => onUpdateStepDate(item.devis_id, 'envoye', picked)} acting={acting} disabled={!item.devis_id} testid="edit-date-envoye" />
        </div>
      ) : (
        <div className="text-center py-6 space-y-3">
          <p className="text-sm text-slate-500">Le devis n'a pas encore été marqué comme envoyé.</p>
          {item.devis_id && (
            <StepMarkAction label="Date d'envoi" picked={picked} setPicked={setPicked}
              onMark={() => onMarkEnvoye(item.devis_id, picked)} acting={acting === item.devis_id}
              color="indigo" icon={Send} ctaLabel="Marquer comme envoyé" testid="mark-envoye-btn" />
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================
// STEP: ACCEPTÉ (Devis signé)
// ==========================================
function StepAccepte({ item, acting, onMarkAccepte, onUpdateStepDate }) {
  const date = item.step_dates?.accepte;
  const [picked, setPicked] = useState(toYmd(date) || toYmd(new Date().toISOString()));
  if (item.devis_statut === 'Refusé') {
    return (
      <div>
        <StepHeader icon={X} title="Devis refusé" color="slate" />
        <div className="p-4 bg-red-50 border border-red-100 rounded-lg text-sm text-red-900">Le devis a été refusé par la collectivité.</div>
      </div>
    );
  }
  return (
    <div>
      <StepHeader icon={CheckCircle2} title="Devis signé" subtitle={date ? `Signé le ${fmtDate(date)}` : 'Pas encore signé'} color="emerald" />
      {date ? (
        <div className="space-y-3">
          <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-lg space-y-1">
            <p className="text-sm text-emerald-900">Le devis a été signé par le client le <strong>{fmtDate(date)}</strong>.</p>
            <p className="text-xs text-emerald-700">Prochaine étape : réception du bon de commande.</p>
          </div>
          <StepDateEditor label="Modifier la date de signature" value={picked} setValue={setPicked}
            onSave={() => onUpdateStepDate(item.devis_id, 'accepte', picked)} acting={acting} disabled={!item.devis_id} testid="edit-date-accepte" />
        </div>
      ) : (
        <div className="text-center py-6 space-y-3">
          <p className="text-sm text-slate-500">Le devis n'est pas encore signé.</p>
          {item.devis_id && (
            <StepMarkAction label="Date de signature" picked={picked} setPicked={setPicked}
              onMark={() => onMarkAccepte(item.devis_id, picked)} acting={acting === item.devis_id}
              color="emerald" icon={CheckCircle2} ctaLabel="Marquer devis signé" testid="mark-accepte-btn" />
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================
// STEP: BC reçu
// ==========================================
function StepBonCommande({ item, acting, onMarkBc, onUpdateStepDate }) {
  const bc = item.bon_commande;
  const [picked, setPicked] = useState(toYmd(bc?.date) || toYmd(new Date().toISOString()));
  return (
    <div>
      <StepHeader icon={FileSignature} title="Bon de commande" subtitle={bc?.received ? `Reçu le ${fmtDate(bc.date)}` : 'Pas encore reçu'} color="amber" />
      {bc?.received ? (
        <div className="space-y-3">
          <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg space-y-1">
            <p className="text-sm text-amber-900">Bon de commande reçu le <strong>{fmtDate(bc.date)}</strong>.</p>
            <p className="text-xs text-amber-700">{item.chantier_id ? `Chantier ${item.chantier_id} créé automatiquement.` : 'Le chantier sera créé dès que le devis sera marqué Accepté.'}</p>
          </div>
          <StepDateEditor label="Modifier la date du BC" value={picked} setValue={setPicked}
            onSave={() => onUpdateStepDate(item.devis_id, 'bon_commande', picked)} acting={acting} disabled={!item.devis_id} testid="edit-date-bc" />
        </div>
      ) : (
        <div className="text-center py-6 space-y-3">
          <p className="text-sm text-slate-500">Bon de commande pas encore reçu.</p>
          {item.devis_id && (
            <StepMarkAction label="Date du BC" picked={picked} setPicked={setPicked}
              onMark={() => onMarkBc(item.devis_id, picked)} acting={acting === item.devis_id}
              color="amber" icon={FileSignature} ctaLabel="Marquer le BC reçu" testid="mark-bc-btn" />
          )}
        </div>
      )}
    </div>
  );
}

// Helpers: convert any ISO date into YYYY-MM-DD for <input type="date">
function toYmd(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return typeof iso === 'string' && iso.length >= 10 ? iso.slice(0, 10) : '';
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  } catch { return ''; }
}

function StepMarkAction({ label, picked, setPicked, onMark, acting, color, icon: Icon, ctaLabel, testid }) {
  const btnClass = {
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    amber: 'bg-amber-600 hover:bg-amber-700',
    orange: 'bg-orange-600 hover:bg-orange-700',
    blue: 'bg-blue-600 hover:bg-blue-700',
    slate: 'bg-slate-700 hover:bg-slate-800',
  }[color] || 'bg-blue-600 hover:bg-blue-700';
  return (
    <div className="inline-flex flex-col sm:flex-row items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-slate-500" />
        <div className="text-left">
          <label className="block text-[11px] text-slate-500">{label}</label>
          <Input type="date" value={picked} onChange={(e) => setPicked(e.target.value)}
            className="h-8 text-sm" data-testid={`${testid}-date`} />
        </div>
      </div>
      <Button onClick={onMark} disabled={acting} className={`${btnClass} text-white`} data-testid={testid}>
        {acting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Icon className="w-4 h-4 mr-1" />}
        {ctaLabel}
      </Button>
      <p className="text-[11px] text-slate-400 sm:ml-2">Si vide, la date du jour est utilisée.</p>
    </div>
  );
}

function StepDateEditor({ label, value, setValue, onSave, acting, disabled, testid }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} disabled={disabled}
        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 disabled:opacity-40"
        data-testid={`${testid}-open`}>
        <Edit className="w-3.5 h-3.5" /> {label}
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
      <Calendar className="w-4 h-4 text-slate-500" />
      <Input type="date" value={value} onChange={(e) => setValue(e.target.value)} className="h-8 text-sm" data-testid={`${testid}-date`} />
      <Button size="sm" onClick={() => { onSave(); setOpen(false); }} disabled={acting || disabled} data-testid={testid}>
        {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
      </Button>
      <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
    </div>
  );
}

// ==========================================
// STEP: CHANTIER (embeds the full ChantierDetail page inline)
// ==========================================
function StepChantier({ item, reload, onUpdateStepDate, acting }) {
  const [pickedDate, setPickedDate] = useState(toYmd(item.step_dates?.chantier) || toYmd(new Date().toISOString()));
  if (!item.chantier_id) {
    return (
      <div>
        <StepHeader icon={HardHat} title="Chantier" color="slate" />
        <div className="text-center py-8 space-y-2">
          <p className="text-sm text-slate-500">Le chantier sera créé automatiquement dès que :</p>
          <ul className="text-xs text-slate-600 space-y-1 max-w-md mx-auto">
            <li>✅ le devis est marqué <strong>Accepté</strong></li>
            <li>✅ le bon de commande est marqué <strong>Reçu</strong></li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div>
      <StepHeader icon={HardHat} title="Chantier" subtitle={`Lancé le ${fmtDate(item.step_dates?.chantier) || '—'}`} color="orange" />
      <div className="mb-4">
        <StepDateEditor label="Modifier la date de lancement du chantier" value={pickedDate} setValue={setPickedDate}
          onSave={() => onUpdateStepDate(item.devis_id, 'chantier', pickedDate)}
          acting={acting} disabled={!item.devis_id} testid="edit-date-chantier" />
      </div>

      {/* Top summary (kept as-is per user request) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <InfoRow label="Référence" value={item.chantier_id} mono />
          <InfoRow label="Statut chantier" value={item.chantier_statut} />
          <InfoRow label="Adresse" value={item.adresse} />
        </div>
        <div>
          <InfoRow label="Budget HT prévu" value={fmtMoney(item.devis_montant_ht)} />
          <InfoRow label="Montant total TTC" value={fmtMoney(item.devis_montant_ttc)} />
        </div>
      </div>

      {/* Full ChantierDetail page rendered inline */}
      <div className="border-t border-slate-200 pt-6">
        <ChantierDetail chantierId={item.chantier_id} embedded />
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color }) {
  const colorMap = {
    slate:'bg-slate-100 text-slate-700',
    blue:'bg-blue-100 text-blue-700',
    indigo:'bg-indigo-100 text-indigo-700',
    emerald:'bg-emerald-100 text-emerald-700',
    orange:'bg-orange-100 text-orange-700',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorMap[color] || colorMap.slate}`}><Icon className="w-4 h-4" /></div>
        <div><p className="text-2xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
      </div>
    </div>
  );
}
